// Manager multi-select dropdown for the /requests advanced filter panel.
//
// WHY: kept local to this feature (no shared MultiSelect primitive yet)
//   because there is exactly one consumer; promotion can wait until a
//   second real consumer surfaces. The UI is a button + popup with
//   `role="menu"` and `role="menuitemcheckbox"` rows. Toggling a row
//   keeps the popup open so several managers can be picked in one trip;
//   Escape and outside-click close it via the shared `useDismiss`
//   primitive. Keyboard navigation (Up/Down/Home/End) is handled here
//   rather than via `ui/Menu.tsx` because that primitive's internal
//   handler queries `[role="menuitem"]` only and would skip
//   `menuitemcheckbox` rows.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequestManagerOptions } from '@magimp/shared-hooks';
import { Icon, useDismiss } from '../../../ui/index.js';

const ITEM_SELECTOR = '[role="menuitem"],[role="menuitemcheckbox"]';

export interface ManagerMultiPickerFieldProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export function ManagerMultiPickerField(props: ManagerMultiPickerFieldProps) {
  const { value, onChange } = props;
  const { t } = useTranslation('requests');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const optionsQuery = useRequestManagerOptions();
  const options = optionsQuery.data ?? [];

  useDismiss({ ref: wrapperRef, enabled: open, onDismiss: () => setOpen(false) });

  // INVARIANT: focus management mirrors WAI-ARIA menu pattern.
  //   - on open: move focus to the first menu row so arrow-key navigation
  //     works without an extra Tab.
  //   - on close (Escape, outside click, clear): return focus to the
  //     opener button so AT users don't land on a detached element.
  //   `wasOpenRef` keeps the close-side branch from firing on initial
  //   mount when `open` starts false.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      const first = menuRef.current?.querySelector<HTMLElement>(ITEM_SELECTOR);
      first?.focus();
    } else if (wasOpenRef.current) {
      buttonRef.current?.focus();
    }
    wasOpenRef.current = open;
  }, [open]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const closedLabel = ((): string => {
    if (value.length === 0) return t('list.filterPanel.managerAll');
    if (value.length === 1) {
      const id = value[0];
      const name = id ? options.find((o) => o.id === id)?.name : undefined;
      return name ?? t('list.filterPanel.manager');
    }
    return t('list.filterPanel.managerSelectedCount', { count: value.length });
  })();

  const toggle = (id: string): void => {
    if (selectedSet.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const clear = (): void => {
    onChange([]);
    setOpen(false);
  };

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? items.indexOf(active) : -1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = idx < 0 ? 0 : Math.min(idx + 1, items.length - 1);
      items[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = idx < 0 ? items.length - 1 : Math.max(idx - 1, 0);
      items[next]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}
    >
      <span
        id="requests-filter-manager-label"
        style={{
          fontSize: 10,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          fontWeight: 600,
          color: 'var(--fg-3)',
        }}
      >
        {t('list.filterPanel.manager')}
      </span>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        // INVARIANT: the accessible name must reflect both the field
        //   purpose AND the current selection so AT users hear what
        //   they would see ("Manager: 2 managers selected") rather than
        //   just the static label. Concatenating two ids lets the
        //   visible field label and the dynamic value span both
        //   contribute to the computed name.
        aria-labelledby="requests-filter-manager-label requests-filter-manager-value"
        data-testid="requests-filter-manager"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flex: 1,
          height: 30,
          padding: '0 8px',
          borderRadius: 'var(--r-1)',
          border: '1px solid var(--border-1)',
          background: 'var(--bg-1)',
          color: 'var(--fg-1)',
          fontSize: 12,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          id="requests-filter-manager-value"
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {closedLabel}
        </span>
        <Icon name="chevron-down" size={14} style={{ color: 'var(--fg-3)' }} />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          tabIndex={-1}
          aria-labelledby="requests-filter-manager-label"
          onKeyDown={onMenuKeyDown}
          data-testid="requests-filter-manager-menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 280,
            overflowY: 'auto',
            background: 'var(--bg-1)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--r-1)',
            boxShadow: 'var(--shadow-2)',
            zIndex: 10,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={clear}
            data-testid="requests-filter-manager-clear"
            style={rowStyle()}
          >
            <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center' }} />
            <span style={{ flex: 1, textAlign: 'left' }}>{t('list.filterPanel.managerAll')}</span>
          </button>
          {options.map((opt) => {
            const checked = selectedSet.has(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={checked}
                onClick={() => toggle(opt.id)}
                data-testid={`requests-filter-manager-option-${opt.id}`}
                style={rowStyle()}
              >
                <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center' }}>
                  {checked && <Icon name="check" size={14} style={{ color: 'var(--accent-1)' }} />}
                </span>
                <span style={{ flex: 1, textAlign: 'left' }}>{opt.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function rowStyle(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    height: 28,
    padding: '0 8px',
    borderRadius: 'var(--r-1)',
    border: 'none',
    background: 'transparent',
    color: 'var(--fg-1)',
    fontSize: 12,
    cursor: 'pointer',
    textAlign: 'left',
  };
}
